// WebAuthn (passkey) helpers: convert between the base64url strings the server
// exchanges and the ArrayBuffers the browser credential API expects.

export const isPasskeySupported = (): boolean =>
    typeof window !== 'undefined' && !!window.PublicKeyCredential && !!navigator.credentials

const b64urlToBuffer = (value: string): ArrayBuffer => {
    const padded = value.replace(/-/g, '+').replace(/_/g, '/')
    const binary = atob(padded + '='.repeat((4 - (padded.length % 4)) % 4))
    const bytes = new Uint8Array(binary.length)
    for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i)
    return bytes.buffer
}

const bufferToB64url = (buffer: ArrayBuffer): string => {
    const bytes = new Uint8Array(buffer)
    let binary = ''
    for (const b of bytes) binary += String.fromCharCode(b)
    return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '')
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const prepareCreation = (publicKey: any): PublicKeyCredentialCreationOptions => {
    publicKey.challenge = b64urlToBuffer(publicKey.challenge)
    publicKey.user.id = b64urlToBuffer(publicKey.user.id)
    if (publicKey.excludeCredentials) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        publicKey.excludeCredentials.forEach((c: any) => { c.id = b64urlToBuffer(c.id) })
    }
    return publicKey
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const prepareRequest = (publicKey: any): PublicKeyCredentialRequestOptions => {
    publicKey.challenge = b64urlToBuffer(publicKey.challenge)
    if (publicKey.allowCredentials) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        publicKey.allowCredentials.forEach((c: any) => { c.id = b64urlToBuffer(c.id) })
    }
    return publicKey
}

export const attestationToJSON = (credential: PublicKeyCredential) => {
    const response = credential.response as AuthenticatorAttestationResponse
    return {
        id:                      credential.id,
        rawId:                   bufferToB64url(credential.rawId),
        type:                    credential.type,
        clientExtensionResults:  credential.getClientExtensionResults(),
        authenticatorAttachment: credential.authenticatorAttachment ?? undefined,
        response:                {
            attestationObject: bufferToB64url(response.attestationObject),
            clientDataJSON:    bufferToB64url(response.clientDataJSON),
        },
    }
}

export const assertionToJSON = (credential: PublicKeyCredential) => {
    const response = credential.response as AuthenticatorAssertionResponse
    return {
        id:                      credential.id,
        rawId:                   bufferToB64url(credential.rawId),
        type:                    credential.type,
        clientExtensionResults:  credential.getClientExtensionResults(),
        authenticatorAttachment: credential.authenticatorAttachment ?? undefined,
        response:                {
            authenticatorData: bufferToB64url(response.authenticatorData),
            clientDataJSON:    bufferToB64url(response.clientDataJSON),
            signature:         bufferToB64url(response.signature),
            userHandle:        response.userHandle ? bufferToB64url(response.userHandle) : null,
        },
    }
}
